import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, Star, Trash2 } from "lucide-react";
import { Field, Text, Textarea, Select, Toggle, StringListEditor } from "@/admin/components/AdminField";
import ImageUpload, { GalleryUpload } from "@/admin/components/ImageUpload";
import POIEditor from "@/admin/components/POIEditor";
import WaypointEditor from "@/admin/components/WaypointEditor";
import RichTextEditor from "@/admin/components/RichTextEditor";

const EMPTY = {
  slug: "", name: "", state: "", region: "", start_city: "", end_city: "",
  distance_km: 0, difficulty: "Fácil", terrain: "Asfalto", estimated_time: "", bike_type: "", best_season: "",
  short_description: "", description: "", history: "", technical_info: "", careful_tips: "",
  cover_image: "", hero_image: "", gallery: [],
  google_maps_url: "", video_url: "",
  waypoints: [], waypoints_geo: [], highlights: [], tips: [], points_of_interest: [], reviews: [],
  related_slugs: [], verified: true, published: true,
  seo: { meta_title: "", meta_description: "", og_image: "" },
};

export default function RouteForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      adminApi.get(`/admin/routes/${id}`)
        .then((r) => setF({ ...EMPTY, ...r.data, seo: { ...EMPTY.seo, ...(r.data.seo || {}) } }))
        .catch(() => toast.error("Falha ao carregar"))
        .finally(() => setLoading(false));
    }
  }, [id, editing]);

  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  const setSeo = (patch) => setF((s) => ({ ...s, seo: { ...s.seo, ...patch } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await adminApi.put(`/admin/routes/${id}`, f);
        toast.success("Rota atualizada");
      } else {
        await adminApi.post(`/admin/routes`, f);
        toast.success("Rota criada");
      }
      navigate("/admin/rotas");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao salvar");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-[#B8B8B8]"><Loader2 className="inline animate-spin" size={16} /> Carregando…</div>;

  return (
    <form onSubmit={submit} className="space-y-10 max-w-5xl" data-testid="route-form">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <Link to="/admin/rotas" className="text-[10px] tracking-[0.25em] uppercase text-[#B8B8B8] inline-flex items-center gap-2 hover:text-[#C6A762]">
            <ArrowLeft size={12} /> Voltar
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl mt-3">{editing ? "Editar rota" : "Nova rota"}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <Toggle value={f.verified} onChange={(v) => set({ verified: v })} label="Rota Verificada pela ROTTA" />
          <Toggle value={f.published} onChange={(v) => set({ published: v })} label="Publicada" />
          <button data-testid="route-save" disabled={saving} className="btn-gold px-6 py-3 rounded-full text-xs inline-flex items-center gap-2 disabled:opacity-60 ml-auto sm:ml-0">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
          </button>
        </div>
      </div>

      <Section title="Informações Básicas">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Nome da rota"><Text data-testid="route-name" value={f.name} onChange={(v) => set({ name: v, slug: f.slug || v })} required /></Field>
          <Field label="Slug (URL)"><Text value={f.slug} onChange={(v) => set({ slug: v })} placeholder="rota-capitao-senra" /></Field>
          <Field label="Estado"><Text value={f.state} onChange={(v) => set({ state: v })} placeholder="Minas Gerais" required /></Field>
          <Field label="Região"><Text value={f.region} onChange={(v) => set({ region: v })} placeholder="Região Metropolitana de BH" /></Field>
          <Field label="Cidade inicial"><Text value={f.start_city} onChange={(v) => set({ start_city: v })} /></Field>
          <Field label="Cidade final"><Text value={f.end_city} onChange={(v) => set({ end_city: v })} /></Field>
          <Field label="Distância (km)"><Text type="number" step="0.1" value={f.distance_km} onChange={(v) => set({ distance_km: parseFloat(v) || 0 })} /></Field>
          <Field label="Tempo médio"><Text value={f.estimated_time} onChange={(v) => set({ estimated_time: v })} placeholder="4 a 6 horas" /></Field>
          <Field label="Grau de dificuldade">
            <Select value={f.difficulty} onChange={(v) => set({ difficulty: v })}
              options={[{ value: "Fácil", label: "Fácil" }, { value: "Moderada", label: "Moderada" }, { value: "Avançada", label: "Avançada" }]} />
          </Field>
          <Field label="Tipo de terreno">
            <Select value={f.terrain} onChange={(v) => set({ terrain: v })}
              options={[{ value: "Asfalto", label: "Asfalto" }, { value: "Terra", label: "Terra" }, { value: "Misto", label: "Misto" }]} />
          </Field>
          <Field label="Tipo de moto recomendada"><Text value={f.bike_type} onChange={(v) => set({ bike_type: v })} placeholder="Big Trail / Touring" /></Field>
          <Field label="Melhor época do ano"><Text value={f.best_season} onChange={(v) => set({ best_season: v })} placeholder="Abril a setembro" /></Field>
        </div>
      </Section>

      <Section title="Imagens">
        <div className="grid md:grid-cols-2 gap-5">
          <ImageUpload label="Imagem principal (capa)" value={f.cover_image} onChange={(v) => set({ cover_image: v, hero_image: f.hero_image || v })} folder="rotta/routes" testId="route-cover" />
          <ImageUpload label="Imagem de hero (opcional)" value={f.hero_image} onChange={(v) => set({ hero_image: v })} folder="rotta/routes" />
        </div>
        <div className="mt-5">
          <GalleryUpload value={f.gallery} onChange={(v) => set({ gallery: v })} folder="rotta/gallery" label="Galeria" />
        </div>
      </Section>

      <Section title="Descrição">
        <div className="grid gap-5">
          <Field label="Descrição curta"><Textarea rows={2} value={f.short_description} onChange={(v) => set({ short_description: v })} /></Field>
          <Field label="Descrição / resumo"><Textarea rows={5} value={f.description} onChange={(v) => set({ description: v })} /></Field>
          <Field label="História da rota"><Textarea rows={6} value={f.history} onChange={(v) => set({ history: v })} /></Field>
          <Field label="Informações técnicas"><Textarea rows={3} value={f.technical_info} onChange={(v) => set({ technical_info: v })} /></Field>
          <Field label="Cuidados importantes (resumo)"><Textarea rows={3} value={f.careful_tips} onChange={(v) => set({ careful_tips: v })} /></Field>
        </div>
      </Section>

      <Section title="Links">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Link do Google Maps"><Text value={f.google_maps_url} onChange={(v) => set({ google_maps_url: v })} placeholder="https://maps.app.goo.gl/…" /></Field>
          <Field label="Link do vídeo (YouTube)" hint="Deixe vazio para ocultar a seção de vídeo."><Text value={f.video_url} onChange={(v) => set({ video_url: v })} placeholder="https://youtube.com/watch?v=…" /></Field>
        </div>
      </Section>

      <Section title="Percurso">
        <div className="grid md:grid-cols-2 gap-8">
          <Field label="Pontos de passagem (texto)">
            <StringListEditor value={f.waypoints} onChange={(v) => set({ waypoints: v })} placeholder="Ex: São Sebastião das Águas Claras" testId="waypoints-editor" />
          </Field>
          <Field label="Destaques">
            <StringListEditor value={f.highlights} onChange={(v) => set({ highlights: v })} placeholder="Ex: Serra do Rola-Moça" />
          </Field>
        </div>
        <div className="mt-8">
          <Field label="Coordenadas do trajeto (mapa interativo)">
            <WaypointEditor value={f.waypoints_geo} onChange={(v) => set({ waypoints_geo: v })} />
          </Field>
        </div>
      </Section>

      <Section title="Dicas / Cuidados">
        <Field label="Lista de dicas (aparecem numeradas no site)">
          <StringListEditor value={f.tips} onChange={(v) => set({ tips: v })} placeholder="Ex: Faça a rota com tanque cheio." />
        </Field>
      </Section>

      <Section title="Pontos de Interesse">
        <POIEditor value={f.points_of_interest} onChange={(v) => set({ points_of_interest: v })} />
      </Section>

      <Section title="Rotas Relacionadas">
        <Field label="Slugs das rotas relacionadas" hint="Ex: rota-capitao-senra">
          <StringListEditor value={f.related_slugs} onChange={(v) => set({ related_slugs: v })} placeholder="slug-da-rota" />
        </Field>
      </Section>

      {editing && (
        <Section title="Avaliações">
          <ReviewsManager routeId={id} />
        </Section>
      )}

      <Section title="SEO">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Meta Title"><Text value={f.seo.meta_title} onChange={(v) => setSeo({ meta_title: v })} /></Field>
          <Field label="OG Image URL"><Text value={f.seo.og_image} onChange={(v) => setSeo({ og_image: v })} /></Field>
          <Field label="Meta Description" cols={2}><Textarea rows={2} value={f.seo.meta_description} onChange={(v) => setSeo({ meta_description: v })} /></Field>
        </div>
      </Section>

      <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
        <Link to="/admin/rotas" className="btn-ghost px-6 py-3 rounded-full text-xs">Cancelar</Link>
        <button disabled={saving} className="btn-gold px-6 py-3 rounded-full text-xs inline-flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar rota
        </button>
      </div>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <section className="card-rotta p-4 sm:p-7 bg-[#141414]">
      <h2 className="font-display tracking-wider uppercase text-sm text-[#C6A762] mb-6">{title}</h2>
      {children}
    </section>
  );
}

function ReviewsManager({ routeId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/routes/${routeId}/reviews`);
      setItems(data.reviews || []);
    } catch {
      toast.error("Falha ao carregar avaliações");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [routeId]);

  const removeOne = async (reviewId) => {
    if (!window.confirm("Excluir esta avaliação? Essa ação não pode ser desfeita.")) return;
    setBusyId(reviewId);
    try {
      await adminApi.delete(`/admin/routes/${routeId}/reviews/${reviewId}`);
      setItems((prev) => prev.filter((r) => r.id !== reviewId));
      toast.success("Avaliação excluída");
    } catch {
      toast.error("Falha ao excluir avaliação");
    } finally {
      setBusyId(null);
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Excluir TODAS as ${items.length} avaliações desta rota? Essa ação não pode ser desfeita.`)) return;
    setClearing(true);
    try {
      await adminApi.delete(`/admin/routes/${routeId}/reviews`);
      setItems([]);
      toast.success("Todas as avaliações foram excluídas");
    } catch {
      toast.error("Falha ao excluir avaliações");
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return <div className="text-[#B8B8B8] text-sm inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Carregando avaliações…</div>;
  }

  if (!items.length) {
    return <p className="text-[#B8B8B8] text-sm">Nenhuma avaliação cadastrada nesta rota.</p>;
  }

  return (
    <div className="space-y-4" data-testid="admin-reviews-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-[#B8B8B8] text-xs uppercase tracking-[0.2em]">{items.length} avaliação(ões) cadastrada(s)</p>
        <button
          type="button"
          onClick={clearAll}
          disabled={clearing}
          data-testid="admin-reviews-clear-all"
          className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300 border border-red-400/40 hover:border-red-300 rounded-full px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60"
        >
          {clearing ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
          Excluir todas
        </button>
      </div>
      <ul className="space-y-3">
        {items.map((r) => (
          <li key={r.id} className="border border-[#1f1f1f] rounded-lg p-4 bg-[#0f0f0f]" data-testid="admin-review-item">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-white font-medium">{r.name || "Anônimo"}</span>
                  <span className="inline-flex items-center gap-1 text-[#C6A762] text-sm">
                    <Star size={12} className="fill-current" /> {r.rating}/5
                  </span>
                  {r.date && <span className="text-[#666] text-xs">{r.date}</span>}
                </div>
                {r.title && <p className="text-white/90 mt-2 text-sm font-medium">{r.title}</p>}
                {r.comment && <p className="text-[#B8B8B8] mt-1 text-sm whitespace-pre-wrap break-words">{r.comment}</p>}
              </div>
              <button
                type="button"
                onClick={() => removeOne(r.id)}
                disabled={busyId === r.id}
                data-testid={`admin-review-delete-${r.id}`}
                className="shrink-0 text-red-400 hover:text-red-300 disabled:opacity-60"
                title="Excluir avaliação"
              >
                {busyId === r.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
